import { ReaddirResult } from '@capacitor/filesystem'
import { IGetPathOpts, IPathKey } from '../../interfaces/services/filesystem/Paths'
import { getDirPath, PATHS_KEYS, getFilePath2 } from './getFilePath'
import {
  createDir,
  readDir,
  readFile,
  removeDir,
  removeFile,
  writeBlob,
  writeFile
} from './filesystem'
import { getInitialDirs, getUserDirs } from '../../consts/filesystem/PATHS.cts'

export class FileSystem {
  private userId?: number

  constructor () {
  }

  async init () {
    const initialDirectories = getInitialDirs()
    for (let i = 0; i < initialDirectories.length; i++) {
      await this.mkdir(initialDirectories[i]).catch(console.error)
    }
  }

  async setUser (userId: number) {
    this.userId = userId
    const userDirs = getUserDirs(userId)
    for (let i = 0; i < userDirs.length; i++) {
      await this.mkdir(userDirs[i])
    }
  }

  async write (path: string, content: any, blob?: boolean): Promise<void> {
    if (!path) throw new Error('Path param is required.')
    if (!content) throw new Error('Content param is required for ' + path)

    if (blob === undefined || !blob) {
      const jsonContent = JSON.stringify(content)
      const maxChunkSize = 5 * 1024 * 1024 // 5MB

      if (jsonContent.length <= maxChunkSize) {
        return await writeFile(path, jsonContent)
      }

      // Split into chunks
      const chunks = this.splitIntoChunks(jsonContent, maxChunkSize)
      await this.clearExistingChunks(path)

      for (let i = 0; i < chunks.length; i++) {
        const chunkPath = i === 0 ? path : this.getChunkPath(path, i)
        await writeFile(chunkPath, chunks[i])
      }
    } else if (content instanceof Blob) {
      return await writeBlob(path, content)
    } else {
      throw new Error('Content is not Blob type')
    }
  }

  async read<T>(path: string): Promise<T | undefined> {
    if (!path) throw new Error('Path param is required.')
    try {
      // Try to read main file first
      const mainContent = await readFile(path)
      if (mainContent === '') {
        return undefined
      }

      // Intentar parsear directamente primero
      try {
        return JSON.parse(mainContent)
      } catch {
        // Si falla, verificar si hay chunks
        const chunks = await this.readAllChunks(path)
        const fullContent = chunks.length > 1 ? chunks.join('') : mainContent
        
        try {
          return JSON.parse(fullContent)
        } catch (error) {
          return fullContent as any
        }
      }
    } catch (error) {
      return undefined
    }
  }

  async mkdir (dirname: string, path?: string): Promise<void> {
    if (!dirname) throw new Error('Path param is required.')
    try {
      await createDir(dirname, path)
    } catch (error) {
      console.error(error)
    }
  }

  async list (path: string): Promise<ReaddirResult> {
    if (!path) throw new Error('Path param is required.')
    return await readDir(path)
  }

  async removeDir (path: string): Promise<void> {
    if (!path) throw new Error('Path param is required.')
    return await removeDir(path)
  }

  async removeFile (path: string): Promise<void> {
    if (!path) throw new Error('Path param is required.')
    await this.clearExistingChunks(path)
    return await removeFile(path)
  }

  getPath (key: IPathKey, opts?: IGetPathOpts): string | undefined {
    const pathKey = PATHS_KEYS.find(pk => pk.key === key)
    if (pathKey !== undefined) {
      const isDirectory = typeof pathKey.isDirectory !== 'function' ? pathKey.isDirectory : pathKey.isDirectory(opts)
      if (isDirectory) {
        return getDirPath(key, this.userId, opts)
      } else {
        return getFilePath2(key, this.userId, opts)
      }
    } else {
      console.warn('Key path not found.')
    }
  }

  private splitIntoChunks (content: string, chunkSize: number): string[] {
    const chunks: string[] = []
    for (let i = 0; i < content.length; i += chunkSize) {
      chunks.push(content.slice(i, i + chunkSize))
    }
    return chunks
  }

  private getChunkPath (originalPath: string, chunkIndex: number): string {
    const lastDotIndex = originalPath.lastIndexOf('.')
    if (lastDotIndex === -1) {
      return `${originalPath}_${chunkIndex}`
    }
    const basePath = originalPath.slice(0, lastDotIndex)
    const extension = originalPath.slice(lastDotIndex)
    return `${basePath}_${chunkIndex}${extension}`
  }

  private async readAllChunks (path: string): Promise<string[]> {
    const chunks: string[] = []
    
    try {
      const mainContent = await readFile(path)
      if (mainContent === '') {
        return chunks
      }
      chunks.push(mainContent)
    } catch {
      return chunks
    }

    // Try to read chunk files - CORREGIDO: no buscar _1.json si no hay chunks
    // Solo buscar chunks si el archivo principal existe y es grande
    if (chunks.length === 0) {
      return chunks
    }

    let chunkIndex = 1
    while (true) {
      const chunkPath = this.getChunkPath(path, chunkIndex)
      try {
        const chunkContent = await readFile(chunkPath)
        if (chunkContent === '') {
          break
        }
        chunks.push(chunkContent)
        chunkIndex++
      } catch {
        // No hay más chunks, salir del loop
        break
      }
    }

    return chunks
  }

  private async clearExistingChunks (path: string): Promise<void> {
    let chunkIndex = 1
    while (true) {
      const chunkPath = this.getChunkPath(path, chunkIndex)
      try {
        await removeFile(chunkPath)
        chunkIndex++
      } catch {
        break
      }
    }
  }
}
